(() => {
  'use strict';

  const STATE_VERSION = 14;
  const STORAGE_KEY = 'pi_500_offer_v14';
  const SEEN_STORAGE_KEY = 'pi_500_offer_seen';
  const LEGACY_SEEN_STORAGE_KEYS = ['pi_500_offer_seen_v11', 'pi_500_offer_seen_v10'];
  const OFFER_LIFETIME_MS = 2 * 60 * 60 * 1000;
  const PHASE_IDLE = 'idle';
  const PHASE_ELIGIBLE = 'eligible';
  const PHASE_QUALIFIED = 'qualified';

  let root;
  let nudgeModal;
  let unlockedModal;
  let applyErrorModal;
  let currentAction = null;
  let lastFocusedElement = null;
  let bypassCheckout = false;
  let operationQueue = Promise.resolve();
  let monitorTimer = null;
  const fallbackTimers = new Set();
  let state = defaultState();
  let lastKnownQuantity = null;

  const config = () => window.PI500OfferConfig || {};

  function defaultState() {
    return {
      version: STATE_VERSION,
      phase: PHASE_IDLE,
      offerId: null,
      originProductId: null,
      qualificationCycle: 0,
      discountVerified: false,
      startedAt: 0,
      updatedAt: 0,
    };
  }

  function normalizeState(candidate) {
    if (!candidate || candidate.version !== STATE_VERSION) return null;
    if (![PHASE_IDLE, PHASE_ELIGIBLE, PHASE_QUALIFIED].includes(candidate.phase)) return null;

    const normalized = {
      ...defaultState(),
      ...candidate,
      originProductId: candidate.originProductId ? String(candidate.originProductId) : null,
      qualificationCycle: Number(candidate.qualificationCycle) || 0,
      discountVerified: Boolean(candidate.discountVerified),
      startedAt: Number(candidate.startedAt) || 0,
      updatedAt: Number(candidate.updatedAt) || 0,
    };

    if (
      normalized.phase !== PHASE_IDLE &&
      (!normalized.startedAt || Date.now() - normalized.startedAt > OFFER_LIFETIME_MS)
    ) {
      return defaultState();
    }

    return normalized;
  }

  function readState() {
    let storedState = null;
    try {
      storedState = normalizeState(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'));
    } catch (_error) {
      storedState = null;
    }

    if (storedState && storedState.phase !== PHASE_IDLE) return storedState;

    const seenAt = readSeenAt();
    if (seenAt) {
      return {
        ...defaultState(),
        phase: PHASE_ELIGIBLE,
        offerId: newOfferId(),
        startedAt: seenAt,
        updatedAt: seenAt,
      };
    }

    return storedState || defaultState();
  }

  function readSeenAt() {
    try {
      let seenAt = Number(localStorage.getItem(SEEN_STORAGE_KEY)) || 0;
      if (!seenAt) {
        for (const legacyKey of LEGACY_SEEN_STORAGE_KEYS) {
          const legacySeenAt = Number(localStorage.getItem(legacyKey)) || 0;
          if (legacySeenAt > seenAt) seenAt = legacySeenAt;
        }
        if (seenAt) localStorage.setItem(SEEN_STORAGE_KEY, String(seenAt));
      }
      if (seenAt && Date.now() - seenAt <= OFFER_LIFETIME_MS) return seenAt;
      if (seenAt) localStorage.removeItem(SEEN_STORAGE_KEY);
    } catch (_error) {
      // Session storage still preserves the current-tab journey.
    }
    return 0;
  }

  function markOfferSeen(seenAt = Date.now()) {
    try {
      localStorage.setItem(SEEN_STORAGE_KEY, String(seenAt));
    } catch (_error) {
      // The current tab remains eligible even if cross-tab storage is unavailable.
    }
  }

  function saveState(nextState) {
    state = normalizeState(nextState) || defaultState();
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_error) {
      // The flow still works on the current page if storage is unavailable.
    }
    syncMonitor();
    return state;
  }

  function transition(patch) {
    return saveState({
      ...state,
      ...patch,
      version: STATE_VERSION,
      updatedAt: Date.now(),
    });
  }

  function isOfferActive(candidate = state) {
    return candidate.phase === PHASE_ELIGIBLE || candidate.phase === PHASE_QUALIFIED;
  }

  function enqueue(operation) {
    const run = operationQueue.catch(() => undefined).then(operation);
    operationQueue = run.catch(() => undefined);
    return run;
  }

  function newOfferId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  async function fetchCart() {
    const response = await fetch(`${config().cartUrl}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('We could not check your cart. Please try again.');
    return rememberCart(await response.json());
  }

  function isFullCart(cart) {
    return Boolean(cart && Array.isArray(cart.items) && Number.isFinite(Number(cart.item_count)));
  }

  function totalQuantity(cart) {
    if (Number.isFinite(Number(cart && cart.item_count))) return Number(cart.item_count);
    return (cart && cart.items || []).reduce((total, item) => total + (Number(item.quantity) || 0), 0);
  }

  function rememberCart(cart) {
    if (!isFullCart(cart)) return cart;
    lastKnownQuantity = totalQuantity(cart);
    return cart;
  }

  function cartFacts(cart, candidate = state) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const quantity = totalQuantity(cart);
    const originStillPresent = Boolean(
      candidate.originProductId &&
      items.some((item) => String(item.product_id) === String(candidate.originProductId) && Number(item.quantity) > 0)
    );

    return {
      quantity,
      originStillPresent,
      qualifies: quantity >= 2,
    };
  }

  function normalizedCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  function positiveAmount(value) {
    return Number(value) > 0;
  }

  function cartHasOfferDiscount(cart) {
    const wantedCode = normalizedCode(config().code);
    if (!cart || !wantedCode) return false;

    const applications = [];
    const addApplication = (candidate, amount = 0, applicable = true) => {
      if (!candidate || applicable === false) return;
      const code = candidate.code || candidate.title || candidate.name || candidate;
      applications.push({ code: normalizedCode(code), amount: Number(amount) || 0 });
    };

    (cart.discount_codes || []).forEach((discount) => {
      addApplication(discount, discount.amount, discount.applicable);
    });
    (cart.cart_level_discount_applications || []).forEach((discount) => {
      addApplication(discount, discount.total_allocated_amount || discount.amount);
    });

    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => addApplication(discount, discount.amount));
      (item.discount_allocations || item.line_level_discount_allocations || []).forEach((allocation) => {
        addApplication(
          allocation.discount_application || allocation,
          allocation.amount || allocation.discounted_amount
        );
      });
    });

    const exactApplication = applications.find((application) => application.code === wantedCode);
    if (!exactApplication) return false;

    return positiveAmount(exactApplication.amount) || positiveAmount(cart.total_discount);
  }

  function sectionRequestBody(discount) {
    const cartItems = document.querySelector('cart-drawer-items, cart-items');
    const dynamicSections = cartItems && typeof cartItems.getSectionsToRender === 'function'
      ? cartItems.getSectionsToRender().map((section) => section.section).filter(Boolean)
      : [];
    return {
      discount,
      sections: [...new Set([...dynamicSections, 'cart-drawer', 'cart-icon-bubble'])],
      sections_url: `${window.location.pathname}${window.location.search}`,
    };
  }

  function renderCartSections(cart) {
    if (!cart || !cart.sections) return;
    const cartItems = document.querySelector('cart-drawer-items, cart-items');
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return;

    cartItems.getSectionsToRender().forEach((section) => {
      const sectionHtml = cart.sections[section.section];
      const host = document.getElementById(section.id);
      if (!sectionHtml || !host) return;

      const target = section.selector ? host.querySelector(section.selector) : host;
      if (!target) return;

      target.innerHTML = typeof cartItems.getSectionInnerHTML === 'function'
        ? cartItems.getSectionInnerHTML(sectionHtml, section.selector)
        : sectionHtml;
    });
  }

  function publishCartUpdate(cart, source) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: cart });
  }

  async function updateDiscountInside(discount, source) {
    const response = await fetch(`${config().cartUpdateUrl}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(sectionRequestBody(discount)),
    });
    const cart = await response.json().catch(() => null);
    if (!response.ok || !isFullCart(cart)) {
      throw new Error((cart && (cart.description || cart.message)) || 'Shopify could not update the discount.');
    }

    rememberCart(cart);
    renderCartSections(cart);
    publishCartUpdate(cart, source);
    return cart;
  }

  async function applyOfferDiscountInside(existingCart = null) {
    let cart = isFullCart(existingCart) ? existingCart : await fetchCart();
    if (totalQuantity(cart) < 2) return { applied: false, cart };

    if (!cartHasOfferDiscount(cart)) {
      cart = await updateDiscountInside(config().code, 'pi-500-offer-apply');
    }

    if (!cartHasOfferDiscount(cart)) {
      cart = await fetchCart();
    }

    if (!cartHasOfferDiscount(cart)) {
      throw new Error('The ₹500 code was not accepted for this cart. Please check the Shopify discount settings.');
    }

    return { applied: true, cart };
  }

  function firstCartProduct(cart) {
    const item = (cart.items || [])[0];
    if (!item) return {};
    return {
      id: String(item.product_id),
      title: item.product_title || item.title || '',
      image: item.featured_image && item.featured_image.url ? item.featured_image.url : item.image || '',
    };
  }

  async function clearOwnedDiscountInside(existingCart = null) {
    if (!config().cartUpdateUrl) return false;
    try {
      const cart = isFullCart(existingCart) ? existingCart : await fetchCart();
      if (!cartHasOfferDiscount(cart)) return true;
      await updateDiscountInside('', 'pi-500-offer-clear');
      return true;
    } catch (_error) {
      return false;
    }
  }

  async function armSessionInside(originProductId = null) {
    const wasActive = isOfferActive();
    const shouldClearCode = state.discountVerified;
    const now = Date.now();
    const startedAt = wasActive && state.startedAt ? state.startedAt : now;

    markOfferSeen(startedAt);

    saveState({
      ...defaultState(),
      phase: PHASE_ELIGIBLE,
      offerId: wasActive && state.offerId ? state.offerId : newOfferId(),
      originProductId: originProductId ? String(originProductId) : null,
      qualificationCycle: wasActive ? state.qualificationCycle : 0,
      startedAt,
      updatedAt: now,
    });

    if (shouldClearCode) await clearOwnedDiscountInside();
    return state;
  }

  async function rearmOfferInside(originProductId = state.originProductId) {
    const shouldClearCode = state.discountVerified;
    transition({
      phase: PHASE_ELIGIBLE,
      originProductId: originProductId ? String(originProductId) : null,
      qualificationCycle: state.qualificationCycle + 1,
      discountVerified: false,
    });
    if (unlockedModal && !unlockedModal.hidden) closeModal();
    if (shouldClearCode) await clearOwnedDiscountInside();
    return state;
  }

  async function reconcileInside(reason, suppliedCart = null, options = {}) {
    let cart = isFullCart(suppliedCart) ? rememberCart(suppliedCart) : await fetchCart();
    let facts = cartFacts(cart);
    const result = { reason, cart, facts, state, justQualified: false };

    if (!isOfferActive()) return result;

    if (facts.quantity === 0) {
      if (state.phase === PHASE_QUALIFIED || state.originProductId || state.discountVerified) {
        await rearmOfferInside(null);
      }
      return { ...result, facts: cartFacts(cart), state, waitingForFirstPiece: true };
    }

    if (!facts.originStillPresent) {
      const replacementOriginId = firstCartProduct(cart).id || null;

      if (state.phase === PHASE_QUALIFIED && facts.quantity < 2) {
        await rearmOfferInside(replacementOriginId);
      } else {
        transition({
          originProductId: replacementOriginId,
          discountVerified: state.phase === PHASE_QUALIFIED ? state.discountVerified : false,
        });
      }

      facts = cartFacts(cart);
    }

    if (state.phase === PHASE_QUALIFIED && facts.quantity < 2) {
      await rearmOfferInside(state.originProductId);
      return { ...result, facts: cartFacts(cart), state, justRearmed: true };
    }

    if (state.phase === PHASE_ELIGIBLE && facts.quantity >= 2) {
      const application = await applyOfferDiscountInside(cart);
      if (!application.applied) return { ...result, cart: application.cart, facts, state };
      cart = application.cart;
      facts = cartFacts(cart);
      transition({ phase: PHASE_QUALIFIED, discountVerified: true });
      if (options.showCongratulations !== false) openUnlocked();
      return { ...result, cart, facts, state, justQualified: true };
    }

    if (state.phase === PHASE_QUALIFIED && facts.quantity >= 2 && !cartHasOfferDiscount(cart)) {
      transition({ phase: PHASE_ELIGIBLE, discountVerified: false });
      const application = await applyOfferDiscountInside(cart);
      cart = application.cart;
      facts = cartFacts(cart);
      transition({ phase: PHASE_QUALIFIED, discountVerified: application.applied });
      return { ...result, cart, facts, state, discountRepaired: application.applied };
    }

    return { ...result, facts: cartFacts(cart), state };
  }

  function requestReconcile(reason, suppliedCart = null, options = {}) {
    return enqueue(() => reconcileInside(reason, suppliedCart, options)).catch(() => undefined);
  }

  function scheduleFallbackChecks(reason) {
    [250, 850, 1800].forEach((delay) => {
      const timer = window.setTimeout(() => {
        fallbackTimers.delete(timer);
        requestReconcile(`${reason}-${delay}`);
      }, delay);
      fallbackTimers.add(timer);
    });
  }

  function syncMonitor() {
    if (isOfferActive() && !monitorTimer) {
      monitorTimer = window.setInterval(() => requestReconcile('active-monitor'), 1000);
      return;
    }
    if (!isOfferActive() && monitorTimer) {
      window.clearInterval(monitorTimer);
      monitorTimer = null;
    }
  }

  function productDataFromForm(productForm) {
    if (!productForm) return {};
    return {
      id: productForm.dataset.piProductId || '',
      title: productForm.dataset.piProductTitle || '',
      image: productForm.dataset.piProductImage || '',
    };
  }

  function setNudgeProduct(product) {
    const image = root.querySelector('[data-pi-offer-product-image]');
    const fallback = root.querySelector('[data-pi-offer-product-fallback]');
    if (product.image) {
      image.src = product.image;
      image.alt = product.title || 'Product in your cart';
      fallback.hidden = true;
    } else {
      image.removeAttribute('src');
      image.alt = '';
      fallback.hidden = false;
    }
  }

  function openModal(modal, focusSelector) {
    lastFocusedElement = document.activeElement;
    root.hidden = false;
    nudgeModal.hidden = modal !== nudgeModal;
    unlockedModal.hidden = modal !== unlockedModal;
    applyErrorModal.hidden = modal !== applyErrorModal;
    document.body.classList.add('pi-offer-open');
    window.setTimeout(() => {
      const focusTarget = modal.querySelector(focusSelector) || modal.querySelector('button');
      if (focusTarget) focusTarget.focus();
    }, 0);
  }

  function closeModal() {
    if (!root || root.hidden) return;
    root.hidden = true;
    nudgeModal.hidden = true;
    unlockedModal.hidden = true;
    applyErrorModal.hidden = true;
    document.body.classList.remove('pi-offer-open');
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') lastFocusedElement.focus();
  }

  function showNudgeError(message) {
    const error = root.querySelector('[data-pi-offer-error]');
    error.textContent = message;
    error.hidden = !message;
  }

  function showUnlockedError(message) {
    const error = root.querySelector('[data-pi-offer-unlocked-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  function openNudge(action, product) {
    currentAction = action;
    showNudgeError('');
    setNudgeProduct(product);
    root.querySelector('[data-pi-offer-explore]').disabled = false;
    openModal(nudgeModal, '[data-pi-offer-explore]');
  }

  function openUnlocked() {
    if (state.phase !== PHASE_QUALIFIED || !state.discountVerified) return;
    currentAction = null;
    showUnlockedError('');
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.close === 'function') {
      try {
        cartDrawer.close();
      } catch (_error) {
        cartDrawer.classList.remove('active');
      }
    }
    root.querySelector('[data-pi-offer-code]').textContent = config().code;
    root.querySelector('[data-pi-offer-copy-label]').textContent = 'Tap to copy';
    root.querySelector('[data-pi-offer-checkout]').disabled = false;
    openModal(unlockedModal, '[data-pi-offer-copy]');
  }

  function openApplyError(message) {
    currentAction = null;
    const error = root.querySelector('[data-pi-offer-apply-error-message]');
    error.textContent = message || 'The discount could not be verified. Please retry before checkout.';
    root.querySelector('[data-pi-offer-retry]').disabled = false;
    openModal(applyErrorModal, '[data-pi-offer-retry]');
  }

  function retryDiscount() {
    const button = root.querySelector('[data-pi-offer-retry]');
    button.disabled = true;
    enqueue(async () => {
      try {
        const reconciliation = await reconcileInside('manual-discount-retry');
        if (state.phase === PHASE_QUALIFIED && state.discountVerified && cartHasOfferDiscount(reconciliation.cart)) {
          openUnlocked();
          return;
        }
        throw new Error('Add a second piece again before retrying the discount.');
      } catch (error) {
        openApplyError(error.message || 'The discount could not be verified. Please retry.');
      }
    });
  }

  async function addBuyNowProduct(productForm) {
    const form = productForm && productForm.querySelector('form');
    if (!form) throw new Error('Please select your product options again.');

    const formData = new FormData(form);
    formData.delete('sections');
    formData.delete('sections_url');

    const response = await fetch(config().cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'This product could not be added to your cart.');
    }
    return result;
  }

  function exploreUrl() {
    const url = new URL(config().collectionUrl, window.location.origin);
    url.searchParams.set('pi_offer', '1');
    const currentUrl = new URL(window.location.href);
    ['preview_theme_id', 'preview_script_id', '_fd', 'pb'].forEach((parameter) => {
      if (currentUrl.searchParams.has(parameter)) url.searchParams.set(parameter, currentUrl.searchParams.get(parameter));
    });
    return url.toString();
  }

  function cartPermalinkUrl(cart, discountCode = '') {
    const quantitiesByVariant = new Map();
    (cart && cart.items || []).forEach((item) => {
      const variantId = String(item.variant_id || item.id || '');
      const quantity = Math.max(0, Number(item.quantity) || 0);
      if (!variantId || !quantity) return;
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + quantity);
    });

    if (!quantitiesByVariant.size) throw new Error('Your cart is empty. Please add your product again.');

    const lines = [...quantitiesByVariant.entries()]
      .map(([variantId, quantity]) => `${encodeURIComponent(variantId)}:${quantity}`)
      .join(',');
    const rootUrl = String(config().rootUrl || '/').replace(/\/?$/, '/');
    const url = new URL(`${rootUrl}cart/${lines}`, window.location.origin);
    if (discountCode) url.searchParams.set('discount', discountCode);
    return url.toString();
  }

  function discountedCheckoutUrl(cart) {
    return cartPermalinkUrl(cart, config().code);
  }

  function isolatedBuyNowCheckoutUrl(productForm) {
    const form = productForm && productForm.querySelector('form');
    if (!form) throw new Error('Please select your product options again.');
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    if (!variantId) throw new Error('Please select your product options again.');
    return cartPermalinkUrl({ items: [{ id: variantId, quantity }] });
  }

  async function startOfferInside(action) {
    if (action.type === 'buy-now') await addBuyNowProduct(action.productForm);
    const cart = await fetchCart();
    const originProductId = action.product.id || firstCartProduct(cart).id;
    if (!originProductId) throw new Error('Please add your first product again.');

    const facts = cartFacts(cart, { originProductId });
    if (!facts.originStillPresent) throw new Error('Your first product is no longer in the cart.');

    const startedAt = Date.now();
    markOfferSeen(startedAt);
    saveState({
      ...defaultState(),
      phase: PHASE_ELIGIBLE,
      offerId: newOfferId(),
      originProductId: String(originProductId),
      startedAt,
      updatedAt: startedAt,
    });
    return state;
  }

  function acceptOffer() {
    if (!currentAction) return;
    const action = currentAction;
    currentAction = null;
    const button = root.querySelector('[data-pi-offer-explore]');
    button.disabled = true;
    showNudgeError('');

    enqueue(async () => {
      try {
        await startOfferInside(action);
        window.location.assign(exploreUrl());
      } catch (error) {
        currentAction = action;
        showNudgeError(error.message || 'Something went wrong. Please try again.');
        button.disabled = false;
      }
    });
  }

  function dismissOffer() {
    currentAction = null;
    closeModal();
  }

  function nativeCheckout(button) {
    bypassCheckout = true;
    button.click();
    window.setTimeout(() => { bypassCheckout = false; }, 0);
  }

  function continueOriginalAction() {
    const action = currentAction;
    currentAction = null;
    closeModal();
    if (!action) return;

    if (action.type === 'checkout' && action.button) nativeCheckout(action.button);
    if (action.type === 'buy-now' && action.productForm) {
      try {
        window.location.assign(isolatedBuyNowCheckoutUrl(action.productForm));
      } catch (error) {
        openApplyError(error.message || 'Please select your product options again.');
      }
    }
  }

  async function qualifiedCheckoutInside() {
    const validation = await reconcileInside('pre-checkout', null, { showCongratulations: false });
    const facts = cartFacts(validation.cart);
    if (
      state.phase !== PHASE_QUALIFIED ||
      !state.discountVerified ||
      !facts.qualifies ||
      !cartHasOfferDiscount(validation.cart)
    ) return false;

    // Build checkout from Shopify's just-verified cart snapshot. Including the
    // variant quantities and code in the same Shopify cart permalink prevents
    // draft-preview desktop redirects from losing the discounted cart token.
    window.location.assign(discountedCheckoutUrl(validation.cart));
    return true;
  }

  function checkoutFromUnlocked() {
    const button = root.querySelector('[data-pi-offer-checkout]');
    button.disabled = true;
    showUnlockedError('');
    enqueue(async () => {
      try {
        const didCheckout = await qualifiedCheckoutInside();
        if (!didCheckout) {
          button.disabled = false;
          if (!root.hidden) showUnlockedError('Your cart changed. Add a second piece again to unlock ₹500 off.');
        }
      } catch (error) {
        button.disabled = false;
        openApplyError(error.message || 'We could not apply the discount. Please retry before checkout.');
      }
    });
  }

  function handleCheckoutClick(event, button) {
    if (bypassCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    enqueue(async () => {
      try {
        const reconciliation = await reconcileInside('checkout-click');
        const facts = cartFacts(reconciliation.cart);

        if (facts.quantity === 1) {
          const product = firstCartProduct(reconciliation.cart);
          await armSessionInside(product.id);
          openNudge({ type: 'checkout', button, product }, product);
          return;
        }

        if (reconciliation.justQualified) return;

        if (state.phase === PHASE_QUALIFIED && facts.qualifies) {
          await qualifiedCheckoutInside();
          return;
        }

        nativeCheckout(button);
      } catch (error) {
        if (isOfferActive()) {
          openApplyError(error.message || 'We could not apply the discount. Please retry before checkout.');
          return;
        }
        nativeCheckout(button);
      }
    });
  }

  function handleEmptyCartBuyNowClick(event, button, productForm) {
    event.preventDefault();
    event.stopImmediatePropagation();

    enqueue(async () => {
      try {
        const product = productDataFromForm(productForm);
        await armSessionInside(null);
        openNudge({ type: 'buy-now', button, productForm, product }, product);
      } catch (error) {
        openApplyError(error.message || 'We could not check your cart. Please try again.');
      }
    });
  }

  function findBuyNowTarget(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path.find((node) => node && node.matches && node.matches(
      '.shopify-payment-button__button, .shopify-payment-button [role="button"]'
    ));
  }

  function findCheckoutTarget(target) {
    return target && target.closest && target.closest(
      'button[name="checkout"], #CartDrawer-Checkout, #checkout'
    );
  }

  function looksLikeCartMutation(target) {
    return target && target.closest && target.closest(
      'button[name="add"], .product-form__submit, cart-remove-button, .quantity__button, [data-cart-remove], a[href*="/cart/change"]'
    );
  }

  async function copyCode() {
    const code = config().code;
    let copied = false;

    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error('Clipboard API unavailable');
      }

      await Promise.race([
        navigator.clipboard.writeText(code),
        new Promise((_, reject) => {
          window.setTimeout(() => reject(new Error('Clipboard request timed out')), 750);
        }),
      ]);
      copied = true;
    } catch (_clipboardError) {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      copied = document.execCommand('copy');
      textarea.remove();
    }

    root.querySelector('[data-pi-offer-copy-label]').textContent = copied
      ? 'Copied!'
      : 'Copy unavailable — note the code above';
  }

  function bindEvents() {
    document.addEventListener('click', (event) => {
      const checkoutButton = findCheckoutTarget(event.target);
      if (checkoutButton) {
        handleCheckoutClick(event, checkoutButton);
        return;
      }

      const buyNowButton = findBuyNowTarget(event);
      if (buyNowButton) {
        const productForm = buyNowButton.closest('product-form') ||
          document.querySelector('product-form[data-pi-product-id]');
        const knownQuantity = Number.isFinite(lastKnownQuantity)
          ? lastKnownQuantity
          : Number(config().initialCartCount) || 0;
        if (productForm && knownQuantity === 0) {
          handleEmptyCartBuyNowClick(event, buyNowButton, productForm);
        }
        // With an existing cart item, preserve Shopify's original trusted
        // dynamic-checkout click. Cancelling and replaying it can make Shopify
        // show an intermittent "Transaction failed" dialog.
        return;
      }

      if (isOfferActive() && looksLikeCartMutation(event.target)) scheduleFallbackChecks('cart-control');
    }, true);

    root.querySelectorAll('[data-pi-offer-dismiss]').forEach((element) => {
      element.addEventListener('click', dismissOffer);
    });
    root.querySelector('[data-pi-offer-explore]').addEventListener('click', acceptOffer);
    root.querySelector('[data-pi-offer-proceed]').addEventListener('click', continueOriginalAction);
    root.querySelector('[data-pi-offer-copy]').addEventListener('click', copyCode);
    root.querySelector('[data-pi-offer-checkout]').addEventListener('click', checkoutFromUnlocked);
    root.querySelector('[data-pi-offer-keep-shopping]').addEventListener('click', closeModal);
    root.querySelector('[data-pi-offer-retry]').addEventListener('click', retryDiscount);
    root.querySelector('[data-pi-offer-error-keep-shopping]').addEventListener('click', closeModal);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !root.hidden) dismissOffer();
    });

    document.addEventListener('submit', () => {
      if (isOfferActive()) scheduleFallbackChecks('form-submit');
    }, true);

    window.addEventListener('pageshow', () => requestReconcile('pageshow'));
    window.addEventListener('storage', (event) => {
      if (event.key !== SEEN_STORAGE_KEY || isOfferActive()) return;
      const seenAt = readSeenAt();
      if (!seenAt) return;

      saveState({
        ...defaultState(),
        phase: PHASE_ELIGIBLE,
        offerId: newOfferId(),
        startedAt: seenAt,
        updatedAt: seenAt,
      });
      requestReconcile('offer-seen-other-tab');
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') requestReconcile('visibility');
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
        if (isFullCart(event.cartData)) {
          rememberCart(event.cartData);
          requestReconcile('cart-pubsub', event.cartData);
        }
        else requestReconcile('cart-pubsub-fetch');
      });
    }
  }

  function init() {
    root = document.querySelector('[data-pi-offer-root]');
    if (!root || !config().code || !config().cartUpdateUrl || !config().checkoutUrl) return;
    nudgeModal = root.querySelector('[data-pi-offer-nudge]');
    unlockedModal = root.querySelector('[data-pi-offer-unlocked]');
    applyErrorModal = root.querySelector('[data-pi-offer-apply-error]');
    const configuredInitialCount = Number(config().initialCartCount);
    if (Number.isFinite(configuredInitialCount)) lastKnownQuantity = configuredInitialCount;
    saveState(readState());
    bindEvents();
    requestReconcile('initial-load');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
